import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useLoggedInUserData } from "../../../utils/user";
import { routeUrls } from "../../../navigation/routeUrls";
import HeaderView from "../../../components/admin/headerView";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { AdminFormFieldCheckbox, AdminFormFieldSubmit, TSelectboxOption } from "../../../components/admin/formFields";
import AppSearchBox from "../../../components/searchBox";
import { useDebouncedCallback } from "use-debounce";
import { EventListing, EventModify } from "./type";
import Pagination, { TPaginationSelected } from "../../../components/pagination";
import PortalDropdown from "./table-dropdown";
import svgIcons from "../../../assets/svg/svgIcons";
import { useGetInCabConfigQuery, useModifyInCabConfigMutation } from "../../../api/network/incabApiServices";

const ScreenAdminInCabCoaching: FC = ({
}) => {
    const { t } = useTranslation("translation", {
        keyPrefix: "admins.incabcoaching",
    });
    const [userCanEdit, setUserCanEdit] = useState<boolean>(true);
    const [userCanUpdate, setUserCanUpdate] = useState<boolean>(false);
    const [editing, setEditing] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState(10);
    const [events, setEvents] = useState<EventListing[]>([]);
    const [originalEvents, setOriginalEvents] = useState<EventListing[]>([]);
    const navigate = useNavigate();
    const { getUserACLAccessLevel } = useACL();
    const thisOrgIncidentReviewAllowed = useLoggedInUserData("incidentreviewallowed");
    const { data: response, isFetching: isLoadingEvents } = useGetInCabConfigQuery(undefined, { refetchOnMountOrArgChange: true, });
    const [updateEvents] = useModifyInCabConfigMutation();

    useEffect(() => {
        if (
            thisOrgIncidentReviewAllowed !== "1" ||
            getUserACLAccessLevel(ACL_NAMES.ACL_INCAB_COACHING) < ROLE_ACCESS_LEVELS.READ
        ) {
            navigate(routeUrls.dashboard);
        }
    }, []);

    useEffect(() => {
        if (isLoadingEvents)
            return;
        if (response) {
            setOriginalEvents(response?.events)
            setEvents(response?.events)
        }
    }, [response?.events, isLoadingEvents])

    const filteredEvents = useMemo(() => {
        if (!searchKeyword?.trim()) {
            return events;
        }
        return events.filter((event) =>
            event.event_name
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
        );
    }, [events, searchKeyword]);

    const isEventModified = (
        event: EventListing,
        original?: EventListing
    ) => {
        if (!original) return false;
        return (
            event.warning_max !== original.warning_max ||
            event.timeout_minutes !== original.timeout_minutes ||
            event.is_active !== original.is_active
        );
    };
    const hasChanges = events.some((event, index) =>
        isEventModified(event, originalEvents[index])
    );

    useEffect(() => {
        setUserCanUpdate(hasChanges);
    }, [hasChanges]);

    const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
        setSearchKeyword(value);
        setCurrentPage(1);
    }, 500);

    const pagedEvents = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredEvents.slice(start, start + pageSize);
    }, [filteredEvents, currentPage, pageSize]);

    const editEventConfig = () => {
        setUserCanEdit(false);
        setUserCanUpdate(false);
        setEditing(true);
    }
    const cancelEditEventConfig = () => {
        if (response) { setEvents(originalEvents); }
        setEditing(false);
        setUserCanEdit(true);
    }
    const updateEventConfig = async () => {
        const modifiedRules: EventModify[] = events
            .filter((event, index) =>
                isEventModified(event, originalEvents[index])
            )
            .map((event) => ({
                statuscode: event.statuscode,
                warning_max: event.warning_max,
                timeout_minutes: event.timeout_minutes,
                is_active: event.is_active,
            }));
        try {
            const updatedResponse = await updateEvents({
                rules: modifiedRules,
            }).unwrap();
            setOriginalEvents(updatedResponse.events);
            setEditing(false);
            setUserCanEdit(true);
        } catch (err) {
            console.error("Updating in-cab coaching failed", err);
        }
    }
    const hasInvalidRows = events.some((event) => {
        const hasTimeout = event.timeout_minutes != null;
        const hasWarning = event.warning_max != null;
        if (event.is_active) {
            return !hasTimeout || !hasWarning;
        }
        return hasTimeout !== hasWarning;
    });

    return (
        <div className="flex-1 overflow-y-auto">
            <HeaderView
                title={t("heading")} />
            <h2 className="text-accent-blue-dark text-base font-semibold leading-6 px-6">
                {t("sub_heading")}
            </h2>
            <div className="px-6 mt-4 flex flex-row justify-between items-center">
                <span className="text-xl font-bold leading-6">{t("listing_heading")}</span>
                <div className="flex flex-row gap-3 justify-end">
                    {editing &&
                        <AdminFormFieldSubmit
                            buttonDimensionClass={"h-10 w-full md:px-10 px-4 group"}
                            type="button"
                            variant="primary"
                            onClick={cancelEditEventConfig}
                            label="Cancel"
                        />}
                    {editing ?
                        <AdminFormFieldSubmit
                            buttonDimensionClass={"h-10 w-full md:px-10 px-4 group"}
                            type="button"
                            variant="primary"
                            onClick={updateEventConfig}
                            label="Update"
                            disabled={!userCanUpdate || hasInvalidRows}
                        />
                        :
                        <AdminFormFieldSubmit
                            buttonDimensionClass={"h-10 w-full md:px-10 px-4 group"}
                            type="button"
                            variant="primary"
                            onClick={editEventConfig}
                            label="Edit"
                            disabled={!userCanEdit || getUserACLAccessLevel(ACL_NAMES.ACL_INCAB_COACHING) < ROLE_ACCESS_LEVELS.WRITE}
                        />
                    }

                    <AppSearchBox
                        placeholder={t("search_placeholder")}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            debouncedSetSearchKeyword(e.target.value)
                        }}
                        onClear={
                            () => debouncedSetSearchKeyword("")
                        }
                        wrapperClassName="w-50"
                    />
                </div>
            </div>

            <div className="m-6 overflow-x-auto">
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        <col className="w-[25%]" />
                        <col className="w-[30%]" />
                        <col className="w-[30%]" />
                        <col className="w-[15%]" />
                    </colgroup>

                    <thead className="sticky top-0 bg-white border-b border-gray-600">
                        <tr>
                            <th className="px-2 md:px-6 py-4 text-left align-top">
                                <div className="text-l font-semibold text-gray-900">
                                    {t("details.col_name")}
                                </div>
                            </th>

                            <th className="px-2 md:px-6 py-4 text-left align-top">
                                <div className="text-l font-semibold text-gray-900">
                                    {t("details.col_timeout")}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {t("details.col_timeout_min")}
                                </div>
                            </th>

                            <th className="px-2 md:px-6 py-4 text-left align-top">
                                <div className="text-l font-semibold text-gray-900">
                                    {t("details.col_count")}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {t("details.col_count_range")}
                                </div>
                            </th>
                            <th className="px-2 md:px-6 py-4 text-left align-top">
                                <div className="text-l font-semibold text-gray-900">
                                    {t("details.col_active")}
                                </div>
                            </th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                        {isLoadingEvents ? (
                            <tr>
                                <td colSpan={3} className="pt-4 text-center">
                                    <img
                                        src={svgIcons.LoadingAnimation}
                                        alt="loading-animation"
                                        className="inline-block h-12 w-12"
                                    />
                                </td>
                            </tr>
                        ) : pagedEvents.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={3}
                                    className="py-24 text-center text-md font-bold text-gray-400"
                                >
                                    {t("details.no_events")}
                                </td>
                            </tr>
                        ) : (pagedEvents.map((event) => {
                            const hasTimeout = event.timeout_minutes != null;
                            const hasWarning = event.warning_max != null;
                            const timeoutError = !hasTimeout && (event.is_active || hasWarning);
                            const warningError = !hasWarning && (event.is_active || hasTimeout);
                            return <tr
                                key={event.statuscode}
                                className="hover:bg-accent-blue-paleO66"
                            >
                                <td className="px-2 md:px-6 py-5 align-middle">
                                    <span className="text-sm font-medium text-gray-900">
                                        {event.event_name}
                                    </span>
                                </td>

                                <td className="px-2 md:px-6 py-5 align-middle">
                                    <div className="max-w-sm">
                                        <PortalDropdown
                                            min={1}
                                            max={60}
                                            value={event.timeout_minutes != null ? String(event.timeout_minutes) : ""}
                                            placeholder={t("details.timeout_placeholder")}
                                            options={Array.from({ length: 60 }, (_, i) => ({
                                                label: `${i + 1}`,
                                                value: `${i + 1}`,
                                            }))}
                                            error={timeoutError}
                                            onChange={(value) => {
                                                setEvents((prev) =>
                                                    prev.map((e) =>
                                                        e.statuscode === event.statuscode
                                                            ? { ...e, timeout_minutes: value === "" ? null : Number(value) }
                                                            : e
                                                    )
                                                );
                                            }}
                                            disabled={!editing || !event.is_active}
                                        />
                                    </div>
                                </td>

                                <td className="px-2 md:px-6 py-5 align-middle">
                                    <div className="max-w-sm">
                                        <PortalDropdown
                                            min={0}
                                            max={5}
                                            value={event.warning_max != null ? String(event.warning_max) : ""}
                                            placeholder={t("details.count_placeholder")}
                                            options={Array.from({ length: 6 }, (_, i) => ({
                                                label: `${i}`,
                                                value: `${i}`,
                                            }))}
                                            error={warningError}
                                            onChange={(value) => {
                                                setEvents((prev) =>
                                                    prev.map((e) =>
                                                        e.statuscode === event.statuscode
                                                            ? { ...e, warning_max: value === "" ? null : Number(value) }
                                                            : e
                                                    )
                                                );
                                            }}
                                            disabled={!editing || !event.is_active}
                                        />
                                    </div>
                                </td>
                                <td className="px-2 md:px-6 py-5 align-middle">
                                    <AdminFormFieldCheckbox
                                        label=""
                                        id={event.statuscode.toString()}
                                        type="checkbox"
                                        name=""
                                        fullComponentClickable={false}
                                        customWrapperClass="w-fit"
                                        wrapperPaddingClass="p-2"
                                        checked={event.is_active}
                                        onChange={(value) => {
                                            setEvents(prev =>
                                                prev.map(e =>
                                                    e.statuscode === event.statuscode
                                                        ? { ...e, is_active: value.target.checked }
                                                        : e
                                                )
                                            );
                                        }}
                                        disabled={!editing}
                                    />
                                </td>
                            </tr>
                        })
                        )}
                    </tbody>
                </table>
            </div>
            <div className="m-6">
                <Pagination
                    pageSize={pageSize}
                    handlePageSizeChange={(e: TSelectboxOption | null) => {
                        setCurrentPage(1);
                        setPageSize(parseInt(`${e?.value}`));
                    }}
                    totalPages={
                        Math.max(1, Math.ceil(filteredEvents.length / pageSize))
                    }
                    forcePage={currentPage - 1}
                    handlePageClick={(data: TPaginationSelected) => {
                        setCurrentPage(data?.selected + 1)
                    }}
                    paginCurrentPage={currentPage}
                    paginItemsOnCurrentPage={pagedEvents.length}
                    paginTotalItemsCount={filteredEvents.length}
                    wrapperClass={`flex mt-3 items-center`}
                    resultCountPrefixAtBottomLeft={false}
                />
            </div>
        </div>
    )
}

export default ScreenAdminInCabCoaching;